import { formatPhoneNumber } from 'UI/utils';

/**
 * Formats interview date and time based on the selected type
 * @param {Object} interviewData - Form data
 * @returns {string} - Formatted date string
 */
export const formatInterviewDateTime = (interviewData = {}) => {
  const {
    interviewDate,
    interviewTimeFrame,
    interviewTimezone,
    interviewTime,
    interviewFromHour,
    interviewToHour
  } = interviewData;

  if (!interviewDate || !interviewTimeFrame || !interviewTimezone) {
    return '';
  }

  const isFixedHour = interviewTimeFrame.id === 'fixedHour';

  if (isFixedHour) {
    if (!interviewTime) return '';

    const combinedDateTime = combineDateAndTime(interviewDate, interviewTime);

    return formatFixedHour(combinedDateTime, interviewTimezone.initials);
  }
  if (!interviewFromHour || !interviewToHour) return '';

  const fromDateTime = combineDateAndTime(interviewDate, interviewFromHour);
  const toDateTime = combineDateAndTime(interviewDate, interviewToHour);

  return formatTimeRange(fromDateTime, toDateTime, interviewTimezone.initials);
};

const SENDOUT_TAGS = {
  hiringAuthorityFullName: '{{hiring_authority_full_name}}',
  hiringAuthorityName: '{{hiring_authority_name}}',
  hiringAuthorityEmail: '{{hiring_authority_email}}',
  jobOrderTitle: '{{job_order_title}}',
  companyName: '{{company_name}}',
  candidateName: '{{candidate_full_name}}',
  interviews: '{{interview_dates}}',
  candidatePhone: '{{candidate_phone}}',
  candidateFirstName: '{{candidate_name}}',
  candidateEmail: '{{candidate_email}}'
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SENDOUT_TAG_PATTERNS = Object.keys(SENDOUT_TAGS).map(key => {
  const token = SENDOUT_TAGS[key];
  const tagName = token.replace(/{{|}}/g, '').trim();
  return {
    key,
    pattern: new RegExp(`{{\\s*${escapeRegExp(tagName)}\\s*}}`, 'g')
  };
});

export const replaceSendoutTags = (html, valuesByKey) => {
  if (!html) return '';

  let result = html;
  SENDOUT_TAG_PATTERNS.forEach(({ key, pattern }) => {
    const value = valuesByKey?.[key];
    const safeValue = value == null ? '' : escapeHtml(value);
    result = result.replace(pattern, safeValue);
  });

  return result;
};

const getResolvedTagValues = (emailBody, valuesByKey) => {
  if (!emailBody) return [];

  return SENDOUT_TAG_PATTERNS.reduce((resolvedValues, { key, pattern }) => {
    const matches = emailBody.match(pattern) || [];
    const value = valuesByKey?.[key];
    const safeValue = value == null ? '' : escapeHtml(value);

    if (!safeValue) return resolvedValues;

    const nextValues = matches.map(() => safeValue);
    return [...resolvedValues, ...nextValues];
  }, []);
};

export const buildResolvedEmailBody = ({
  emailBody = '',
  hiringAuthority = null,
  candidate = null,
  interviewData = {},
  jobOrder = {},
  candidateInfo = {}
}) => {
  const interviewDateString = formatInterviewDateTime(interviewData);
  const companyName = jobOrder?.company?.name;

  const tagValues = {
    hiringAuthorityFullName: hiringAuthority?.full_name,
    hiringAuthorityName: hiringAuthority?.first_name,
    hiringAuthorityEmail: hiringAuthority?.work_email,
    jobOrderTitle: jobOrder?.title,
    companyName,
    candidateName: candidate?.fullName,
    interviews: interviewDateString,
    candidatePhone: formatPhoneNumber(candidateInfo?.phone),
    candidateFirstName: candidateInfo?.name,
    candidateEmail: candidate?.email
  };

  return {
    resolvedEmailBody: replaceSendoutTags(emailBody, tagValues),
    resolvedTagValues: getResolvedTagValues(emailBody, tagValues)
  };
};

/**
 * Combines the date from one Date object with the time from another
 * @param {Date|string} dateObj - Date object or ISO string with the date
 * @param {Date|string} timeObj - Date object or ISO string with the time
 * @returns {Date} - Combined Date object
 */
const combineDateAndTime = (dateObj, timeObj) => {
  const date = dateObj instanceof Date ? dateObj : new Date(dateObj);
  const time = timeObj instanceof Date ? timeObj : new Date(timeObj);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0,
    0
  );
};

/**
 * Formats a date with fixed hour
 * @param {Date} dateTime - Combined date and time
 * @param {string} timezoneInitials - Timezone initials (e.g., 'CST', 'PST')
 * @returns {string} - Formatted string (e.g., 'Friday February 20, 06:30 PM CST')
 */
const formatFixedHour = (dateTime, timezoneInitials) => {
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const formattedDate = dateFormatter.format(dateTime);
  const formattedTime = timeFormatter.format(dateTime);

  return `${formattedDate}, ${formattedTime} ${timezoneInitials}`;
};

/**
 * Formats a date with time range
 * @param {Date} fromDateTime - Start date and time
 * @param {Date} toDateTime - End date and time
 * @param {string} timezoneInitials - Timezone initials (e.g., 'CST', 'PST')
 * @returns {string} - Formatted string (e.g., 'Friday February 20, 06:30 PM - 09:30 PM PST')
 */
const formatTimeRange = (fromDateTime, toDateTime, timezoneInitials) => {
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const formattedDate = dateFormatter.format(fromDateTime);
  const formattedFromTime = timeFormatter.format(fromDateTime);
  const formattedToTime = timeFormatter.format(toDateTime);

  return `${formattedDate}, ${formattedFromTime} - ${formattedToTime} ${timezoneInitials}`;
};

export default formatInterviewDateTime;
